/**
 * MeetingSettingsButton: 마스터 전용 "회의설정" 드롭다운 버튼.
 *
 * 하위 메뉴:
 * - 발언권 제어 모드 토글
 * - 회의 녹화 시작/중지
 *
 * v1 참조: gridview.html 마크업 (.newHeader_btnItem.open > .viewerHeader__layer.posRight)
 * CSS: .rtc_new3 .newHeader_btn, .viewerHeader__layer, .viewerHeader__layerListBtn
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { floorActions } from '@/store/slices/floorSlice';

interface MeetingSettingsButtonProps {
  onToggleRecording?: () => void;
}

const MeetingSettingsButton: React.FC<MeetingSettingsButtonProps> = ({ onToggleRecording }) => {
  const dispatch = useAppDispatch();
  const recording = useAppSelector((s) => s.recording);
  const floor = useAppSelector((s) => s.floor);
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 닫기
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleToggleOpen = () => setIsOpen((prev) => !prev);

  const handleFloorControl = () => {
    dispatch(floorActions.setEnabled(!floor.isEnabled));
    setIsOpen(false);
  };

  const handleRecording = useCallback(() => {
    if (recording.status === 'pending') return;
    onToggleRecording?.();
    setIsOpen(false);
  }, [recording.status, onToggleRecording]);

  return (
    <div className={`newHeader_btnItem${isOpen ? ' open' : ''}`} ref={menuRef}>
      <button
        type="button"
        className="newHeader_btn newHeader_btn--dropdown"
        onClick={handleToggleOpen}
      >
        <span className="newHeader_btnIcon icoSet"></span>
        <p className="newHeader_btnText">회의설정</p>
      </button>

      {isOpen && (
        <div className="viewerHeader__layer posRight">
          <ul className="viewerHeader__layerList">
            <li>
              <button
                type="button"
                className={`viewerHeader__layerListBtn${recording.isRecording ? ' selected' : ''}`}
                onClick={handleRecording}
                disabled={recording.status === 'pending'}
              >
                {recording.status === 'pending'
                  ? '녹화 준비 중...'
                  : recording.isRecording
                    ? '녹화 중지'
                    : '회의 녹화'}
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default MeetingSettingsButton;
